import React, { useState, useEffect, useContext } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Checkbox } from '@material-ui/core';
import classnames from 'classnames';
import { components } from 'react-select';
import queryString from 'query-string';
import { AppContext } from '../../AppContext';

// SERVICES
import {
  getStates,
  getStateOptions,
  getFieldManager,
  updateFieldManager,
  getFieldManagerStateXRef,
  getFieldManagers,
} from '../../services';
import { DynamicForm } from '../DynamicForm';
import { Autocomplete } from '../Autocomplete';

const styles = theme => ({});

const DEFAULT_STATES = {
  manager: {
    loading: false,
    saving: false,
    error: null,
    result: null,
  },
  managerOptions: {
    loading: false,
    error: null,
    results: null,
  },
  stateXref: {
    loading: false,
    error: null,
    results: null,
  },
  states: {
    loading: false,
    error: null,
    results: null,
  },
};

const StateOption = props => {
  const { isSelected, label, assignedTo } = props;
  return (
    <components.Option {...props}>
      <div className="row-between-center" style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={isSelected}
            color="primary"
            size="small"
            style={{ padding: 0, marginRight: 8, pointerEvents: 'none' }}
          />
          <span>{label}</span>
        </div>
        {!!assignedTo && <span style={{ marginLeft: 12, opacity: 0.7 }}>current assignee: {assignedTo}</span>}
      </div>
    </components.Option>
  );
};

export const FieldManagerStateBulkAssignment = withStyles(styles)(({ classes, history, location }) => {
  const [selectedFieldManager, setSelectedFieldManager] = useState(null);
  const [managerOptions, setManagerOptions] = useState(DEFAULT_STATES.managerOptions);
  const [manager, setManager] = useState(DEFAULT_STATES.manager);
  const [managerStateXRef, setManagerStateXRef] = useState(DEFAULT_STATES.stateXref);
  const [states, setStates] = useState(DEFAULT_STATES.states);
  const [syncAssignments, setSyncAssignments] = useState(true);

  const { fieldManagerId: fieldManagerIdFromQuery } = queryString.parse(
    location?.search || history?.location?.search || '',
  );
  const { dispatch } = useContext(AppContext);

  const autocompleteStyleOptions = {
    option: (styles, { isDisabled, isFocused }) => {
      return {
        ...styles,
        backgroundColor: isFocused ? '#eee' : 'transparent',
        color: isDisabled ? '#ccc' : '#222',
        cursor: isDisabled ? 'not-allowed' : 'default',
        ':active': {
          ...styles[':active'],
          backgroundColor: isFocused ? '#eee' : 'transparent',
        },
      };
    },
    multiValue: styles => ({
      ...styles,
      backgroundColor: 'var(--jmi-blue)',
      borderRadius: 16,
    }),
    multiValueLabel: styles => ({
      ...styles,
      color: '#fff',
      paddingLeft: 10,
      paddingRight: 4,
    }),
    multiValueRemove: styles => ({
      ...styles,
      color: '#fff',
      borderRadius: '0 16px 16px 0',
      ':hover': {
        backgroundColor: 'var(--jmi-blue)',
        color: 'rgba(255, 255, 255, 0.85)',
      },
    }),
  };

  const getIsStateAlreadyAssigned = ({ value /* value == stateId */ }) =>
    managerStateXRef.results?.find(fmx => fmx.stateId === value && fmx.fieldManager)?.fieldManager.name || false;

  const getStatesAsync = async () => {
    setStates({
      ...DEFAULT_STATES.states,
      loading: true,
    });
    try {
      const results = await getStateOptions();

      const stateOptions = results.sort((a, b) => {
        const { label: aLabel } = a;
        const { label: bLabel } = b;

        if (aLabel > bLabel) {
          return 1;
        }
        if (bLabel > aLabel) {
          return -1;
        }
        return 0;
      });

      setStates({
        ...DEFAULT_STATES.states,
        results: stateOptions,
      });
    } catch (error) {
      setStates({
        ...DEFAULT_STATES.states,
        error,
      });
    }
  };

  const getFieldManagersAsync = async () => {
    setManagerOptions({
      ...DEFAULT_STATES.managerOptions,
      loading: true,
    });
    try {
      const results = await getFieldManagers();
      const options = results.map(fm => ({
        label: fm.name,
        value: fm.fieldManagerId,
      }));

      setManagerOptions({
        ...DEFAULT_STATES.managerOptions,
        results: options,
      });

      if (fieldManagerIdFromQuery != null && fieldManagerIdFromQuery !== '') {
        const preselected = options.find(opt => String(opt.value) === String(fieldManagerIdFromQuery));
        if (preselected) {
          setSelectedFieldManager(preselected);
        }
      }
    } catch (error) {
      console.log(error);
      setManagerOptions({
        ...DEFAULT_STATES.managerOptions,
        error,
      });
    }
  };

  const getFieldManagerAsync = async () => {
    setManager({
      ...DEFAULT_STATES.manager,
      loading: true,
    });
    try {
      const result = await getFieldManager(selectedFieldManager.value);

      const updatedManager = {
        ...result,
        fieldManagerStateXref: result.fieldManagerStateXref.map(x => ({
          // ...x,
          label: x.state.name,
          value: x.state.stateId,
        })),
      };

      setManager({
        ...DEFAULT_STATES.manager,
        result: updatedManager,
      });
    } catch (error) {
      console.log(error);
      setManager({
        ...DEFAULT_STATES.manager,
        error,
      });
    }
  };

  const getFieldManagerStateXRefAsync = async () => {
    setManagerStateXRef({
      ...DEFAULT_STATES.stateXref,
      loading: true,
    });
    try {
      const results = await getFieldManagerStateXRef();
      setManagerStateXRef({
        ...DEFAULT_STATES.stateXref,
        results,
      });
    } catch (error) {
      console.log(error);
      setManagerStateXRef({
        ...DEFAULT_STATES.stateXref,
        error,
      });
    }
  };

  const handleStateSelection = val => {
    setManager({
      ...manager,
      result: {
        ...manager.result,
        fieldManagerStateXref: val || [],
      },
    });
  };

  const selectedStates = manager.result?.fieldManagerStateXref || [];

  const handleManagerSelection = val => {
    setSelectedFieldManager(val);
  };

  const handleSave = async () => {
    try {
      setManager({
        ...manager,
        loading: false,
        error: null,
        saving: true,
      });

      // make the stateXref object compatible with the backend
      const updatedManagerObject = {
        ...manager.result,
        fieldManagerStateXref: manager.result.fieldManagerStateXref.map(xref => ({ stateId: xref.value })),
      };

      await updateFieldManager(updatedManagerObject, { syncAssignments });

      // fetch the xref again so the options will be updated
      getFieldManagerStateXRefAsync();

      setManager({
        ...manager,
        saving: false,
      });

      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'success',
          message: 'Manager update successful!',
        },
      });
    } catch (error) {
      console.log(error);
      setManager({
        ...manager,
        saving: false,
        error: error.message || 'Field manager update failed',
      });
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'error',
          message: error.message || 'Manager update failed...',
        },
      });
    }
  };

  const getPageInfo = () => {
    getStatesAsync();
    getFieldManagerAsync();
    getFieldManagerStateXRefAsync();
  };

  useEffect(() => {
    if (selectedFieldManager) {
      getPageInfo();
    }
  }, [selectedFieldManager]);

  useEffect(() => {
    getFieldManagersAsync();
  }, []);

  return (
    <div className="grid-container">
      <div className="row-start-center mb-25">
        <div style={{ width: 275, textAlign: 'left' }}>Select a Field Manager: </div>
        <div style={{ flex: 1 }}>
          <Autocomplete
            isLoading={managerOptions.loading}
            options={managerOptions.results}
            value={selectedFieldManager}
            onChange={handleManagerSelection}
            placeholder="Pick an option before selecting states..."
          />
        </div>
      </div>

      <div
        className="row-start-center mb-25"
        style={{
          marginTop: 50,
          marginBottom: 50,
          pointerEvents:
            !selectedFieldManager || !states.results || manager.loading || manager.saving ? 'none' : 'auto',
        }}
      >
        <div style={{ width: 275, textAlign: 'left' }}>Select States for this Manager: </div>
        <div style={{ flex: 1 }}>
          <Autocomplete
            isLoading={states.loading || manager.loading || managerStateXRef.loading}
            options={states.results}
            value={selectedStates}
            isMulti
            onChange={handleStateSelection}
            styleOptions={autocompleteStyleOptions}
            placeholder="Select states..."
            extraProps={{
              closeMenuOnSelect: false,
              hideSelectedOptions: false,
              components: {
                Option: optionProps => (
                  <StateOption
                    {...optionProps}
                    label={optionProps.data.label}
                    assignedTo={getIsStateAlreadyAssigned(optionProps.data)}
                  />
                ),
              },
            }}
          />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          marginBottom: 50,
        }}
      >
        <div className="row-start-center" style={{ marginBottom: 50 }}>
          <div style={{ width: 275, textAlign: 'left' }}>Update Open Orders:</div>
          <Checkbox
            checked={syncAssignments}
            onChange={e => setSyncAssignments(e.target.checked)}
            color="primary"
            disabled={manager.saving || manager.loading || !manager.result}
            style={{ padding: 0 }}
          />
        </div>
        <div style={{ width: '100%', textAlign: 'center', marginTop: 40 }}>
          {manager.saving ? (
            <CircularProgress size={24} />
          ) : (
            <Button
              disabled={manager.loading || !manager.result}
              size="medium"
              color="primary"
              variant="outlined"
              onClick={handleSave}
            >
              Save
            </Button>
          )}
        </div>

        {manager.error && <p className={classes.textLabelError}>{manager.error}</p>}
        {/* {submitSuccess && <p className={classes.textLabelSuccess}>{submitSuccess}</p>} */}
      </div>

      <p style={{ color: 'rgba(0,0,0,0.5)' }}>
        Note: if you try to assign a state to manager that already has a field manager assigned to it, the state will be
        removed from the existing field manager and assigned to the new manager selected above.
      </p>
    </div>
  );
});
